<template>
    <tr class="kgr-table__row">
        <td class="kgr-table__cell kgr-table__cell--centered">
            <kgr-isbj-status :status="status" />
        </td>
        <td class="kgr-table__cell">
            <a
                v-if="child.kindergarten.hasWriteAccess"
                :href="child.loginLink"
            >
                <span class="kgr-text--bold">{{ child.name.lastName }}</span>
            </a>
        </td>
        <td class="kgr-table__cell">
            <a
                v-if="child.kindergarten.hasWriteAccess"
                :href="child.loginLink"
            >
                <span class="kgr-text--bold">{{ child.name.firstName }}</span>
            </a>
        </td>
        <td class="kgr-table__cell">
            {{ child.kindergarten.name }}
        </td>
        <td
            class="kgr-table__cell kgr-text--noWrap"
            :class="{ diff: hasVoucherNumberDiff() }"
        >
            <template v-if="subvention && subvention.number === contract.voucherNumber">
                <kgr-isbj-voucher-tooltip
                    :voucher-number="subvention.number"
                    :federation-id="federationId"
                />
            </template>
            <template v-else>
                <div class="diffContainer">
                    <div>KigaRoo:</div>
                    <div>
                        <kgr-isbj-voucher-tooltip
                            v-if="subvention && subvention.number"
                            :voucher-number="subvention.number"
                            :federation-id="federationId"
                        />
                        <template v-else>
                            -
                        </template>
                    </div>
                </div>
                <div class="diffContainer">
                    <div>ISBJ:</div>
                    <div>
                        <kgr-isbj-voucher-tooltip
                            v-if="contract.voucherNumber"
                            :voucher-number="contract.voucherNumber"
                            :federation-id="federationId"
                        />
                        <template v-else>
                            -
                        </template>
                    </div>
                </div>
            </template>
        </td>
        <td class="kgr-table__cell kgr-text--noWrap">
            {{ contract.contractNumber }}
        </td>
        <td
            class="kgr-table__cell"
            :class="{ diff: contract.start.hasDiff }"
        >
            <template v-if="contract.start.hasDiff">
                <div class="diffContainer">
                    <div>KigaRoo:</div>
                    <div>{{ contract.start.kigaRoo }}</div>
                </div>
                <div class="diffContainer">
                    <div>ISBJ:</div>
                    <div>{{ contract.start.isbj }}</div>
                </div>
            </template>
            <div v-else>
                {{ contract.start.kigaRoo }}
            </div>
        </td>
        <td
            class="kgr-table__cell"
            :class="{ diff: contract.end.hasDiff }"
        >
            <template v-if="contract.end.hasDiff">
                <div class="diffContainer">
                    <div>KigaRoo:</div>
                    <div>{{ contract.end.kigaRoo }}</div>
                </div>
                <div class="diffContainer">
                    <div>ISBJ:</div>
                    <div>{{ contract.end.isbj }}</div>
                </div>
            </template>
            <div v-else>
                {{ contract.end.kigaRoo }}
            </div>
        </td>
        <td
            class="kgr-table__cell"
            :class="{ diff: contract.scope.hasDiff }"
        >
            <template v-if="contract.scope.hasDiff">
                <div class="diffContainer">
                    <div>KigaRoo:</div>
                    <div>{{ contract.scope.kigaRoo ? $t(`isbj.scope.${contract.scope.kigaRoo}`) : '-' }}</div>
                </div>
                <div class="diffContainer">
                    <div>ISBJ:</div>
                    <div>{{ contract.scope.isbj ? $t(`isbj.scope.${contract.scope.isbj}`) : '-' }}</div>
                </div>
            </template>
            <div v-else>
                {{ contract.scope.kigaRoo ? $t(`isbj.scope.${contract.scope.kigaRoo}`) : '-' }}
            </div>
        </td>
        <td
            class="kgr-table__cell"
            :class="{ diff: child.issues.length > 0 }"
        >
            <div
                v-for="issue in child.issues"
                :key="issue"
            >
                {{ issue }}
            </div>
        </td>
        <td class="kgr-table__cell kgr-table__cell--centeredVertically kgr-table__cell--actions">
            <kgr-icon
                :disabled="buttonStatus.refresh.indexOf(child.id) !== -1"
                icon-class="refresh"
                :title="$t('Daten aktualisieren')"
                @click="refreshChild()"
            />
            <kgr-icon
                disabled
                :pending="subvention && buttonStatus.register.indexOf(subvention.id) !== -1"
                icon-class="sign"
                :title="$t('Vertrag registrieren')"
            />
            <kgr-icon
                :disabled="status.key === 'pending_sync' || contract.matchesContractDates"
                :pending="buttonStatus.contract.indexOf(contract.contractNumber) !== -1"
                icon-class="date_range"
                :title="$t('Vertragslaufzeit anpassen')"
                @click="changeContractPeriod()"
            />
            <kgr-icon
                :disabled="status.key === 'pending_sync' || !contract.scope.hasDiff"
                :pending="buttonStatus.contract.indexOf(contract.contractNumber) !== -1"
                icon-class="clock"
                :title="$t('Betreuungsumfang anpassen')"
                @click="changeScopeOfCare()"
            />
            <kgr-icon
                :disabled="status.key === 'pending_sync' || !contract.allowCancellation"
                :pending="buttonStatus.contract.indexOf(contract.contractNumber) !== -1"
                icon-class="cancel"
                :title="$t('Vertrag stornieren')"
                @click="cancelContract()"
            />
        </td>
    </tr>
</template>

<script>
import Icon from '../../common/icon/Icon';
import IsbjChildRepository from '../../../js/repository/IsbjChildRepository';
import IsbjStatus from './IsbjStatus';
import IsbjVoucherTooltip from './IsbjVoucherTooltip';

export default {
    components: {
        'kgr-icon': Icon,
        'kgr-isbj-status': IsbjStatus,
        'kgr-isbj-voucher-tooltip': IsbjVoucherTooltip,
    },
    props: {
        status: {
            type: Object,
            required: true,
        },
        child: {
            type: Object,
            required: true,
        },
        subvention: {
            type: Object,
            required: false,
        },
        contract: {
            type: Object,
            required: true,
        },
        federationId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            buttonStatus: {
                refresh: [],
                register: [],
                contract: [],
            },
        };
    },
    methods: {
        refreshChild() {
            this.buttonStatus.refresh.push(this.child.id);
            IsbjChildRepository
                .update(this.child.id)
                .then(() => {
                    this.$emit('reload');
                    this.buttonStatus.refresh.splice(this.buttonStatus.refresh.indexOf(this.child.id), 1);
                })
                .catch((error) => this.showErrors(error));
        },
        cancelContract() {
            this.buttonStatus.contract.push(this.contract.contractNumber);
            IsbjChildRepository
                .cancelContract(this.contract.contractNumber)
                .then(() => {
                    this.$emit('reload');
                    this.buttonStatus.contract.splice(this.buttonStatus.contract.indexOf(this.contract.contractNumber), 1);
                })
                .catch((error) => this.showErrors(error));
        },
        changeContractPeriod() {
            this.buttonStatus.contract.push(this.contract.contractNumber);
            IsbjChildRepository
                .updatePeriod(this.contract.contractNumber)
                .then(() => {
                    this.$emit('reload');
                    this.buttonStatus.contract.splice(this.buttonStatus.contract.indexOf(this.contract.contractNumber), 1);
                })
                .catch((error) => this.showErrors(error));
        },
        changeScopeOfCare() {
            this.buttonStatus.contract.push(this.contract.contractNumber);
            IsbjChildRepository
                .changeScope(this.contract.contractNumber)
                .then(() => {
                    this.$emit('reload');
                    this.buttonStatus.contract.splice(this.buttonStatus.contract.indexOf(this.contract.contractNumber), 1);
                })
                .catch((error) => this.showErrors(error));
        },
        hasVoucherNumberDiff() {
            return !this.subvention || !this.subvention.number || this.subvention.number !== this.contract.voucherNumber;
        },
        showErrors(error) {
            if (error.message === 'isbj_api_error') {
                this.$notify.error('ISBJ hat auf die Anfrage mit einer ungültigen Antwort reagiert.');
            } else if (error.message === 'isbj_down') {
                this.$notify.error('Die ISBJ-Schnittstelle ist derzeit nicht erreichbar.');
            } else {
                this.$notify.error('Es ist ein Fehler aufgetreten. Bitte laden Sie die Seite neu.');
            }
        },
    },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
tr.kgr-table__row {
    height: 56px;
    td.kgr-table__cell {
        .kgr-icon {
            vertical-align: middle;
        }
    }
}
.diff {
    background-color: #f9f6c1;
    color: #000;
    .diffContainer {
        display: flex;
        justify-content: space-between;
        div:first-of-type {
            margin-right: 2px;
        }
        div:last-of-type {
            margin-left: 2px;
            white-space: nowrap;
        }
    }
}
</style>
